import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthUser } from '@/types/auth.types';
import { authApi } from '@/lib/api/auth';
import { decodeJWT } from '@/lib/utils/jwt';

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  clearAuth: () => void;
  applyRefreshedTokens: (accessToken: string, refreshToken: string) => void;
  login: (username: string, password: string, tenantCode: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      clearAuth: () => {
        set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false });
      },

      applyRefreshedTokens: (accessToken, refreshToken) => {
        set({ accessToken, refreshToken });
      },

      login: async (username, password, tenantCode) => {
        set({ isLoading: true });
        try {
          const response = await authApi.login({ username, password, tenant_code: tenantCode });
          
          if (!response || !response.access_token) {
            throw new Error('Invalid response from login API: missing access_token');
          }
          
          const user = decodeJWT(response.access_token);
          set({
            user,
            accessToken: response.access_token,
            refreshToken: response.refresh_token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (err) {
          set({ isLoading: false });
          throw err;
        }
      },

      logout: async () => {
        try {
          const currentRefreshToken = get().refreshToken;
          await authApi.logout(currentRefreshToken);
        } finally {
          get().clearAuth();
        }
      },
    }),
    { 
      name: 'epiq-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
